import { DEFAULT_BRAND, normalizeHex } from './brand.js'
import { hexToOklch, oklchToHex } from '../lib/oklch.js'

const NAVIGATION_STYLES = ['neutral', 'tint', 'solid']
/** Source-of-truth literal palette; index.css's `@theme`/`[data-theme]` blocks must mirror these exactly. */
export const PALETTES = {
  light: { surface: '#ffffff', surface2: '#edf1f4', canvas: '#f1f4f6', ink: '#16232d', muted: '#50616e', tertiary: '#5c6d7a', placeholder: '#5c6d7a', line: '#dbe3e9', navigation: '#17232d' },
  dark: { surface: '#19232c', surface2: '#24313c', canvas: '#10171e', ink: '#edf3f7', muted: '#acbac6', tertiary: '#8e9fad', placeholder: '#92a3b1', line: '#2a3945', navigation: '#0c131a' },
}

/**
 * Brand shown while a signed-in tenant's settings have not arrived (or failed
 * to) and nothing is cached for it: the neutral muted ink, never the product
 * blue, so a green tenant never flashes blue (audit S3, rulebook R13).
 */
export const NEUTRAL_BRAND = PALETTES.light.muted

function versionedAsset(url, revision) {
  if (typeof url !== 'string' || !url) return null
  if (!revision || /^(blob|data):/i.test(url)) return url
  const hashIndex = url.indexOf('#')
  const pathAndQuery = hashIndex < 0 ? url : url.slice(0, hashIndex)
  const fragment = hashIndex < 0 ? '' : url.slice(hashIndex)
  const queryIndex = pathAndQuery.indexOf('?')
  const path = queryIndex < 0 ? pathAndQuery : pathAndQuery.slice(0, queryIndex)
  const params = new URLSearchParams(queryIndex < 0 ? '' : pathAndQuery.slice(queryIndex + 1))
  params.set('v', revision)
  return `${path}?${params}${fragment}`
}

/** Accept saved settings, a public profile, or a settings draft; expose only presentation data. */
export function normalizeBranding(input) {
  const source = input && typeof input === 'object' ? input : {}
  const saved = source.branding || {}
  const data = { ...saved, ...source }
  const theme = data.brand_theme || {}
  const revision = data.revision || source.updated_at || null
  return {
    tenant_id: data.tenant_id ?? null,
    application_name: (typeof data.application_name === 'string' && data.application_name.trim()) || source.name || saved.application_name || 'Graphit',
    logo_url: versionedAsset(data.logo_url, revision),
    dark_logo_url: versionedAsset(data.dark_logo_url, revision),
    icon_url: versionedAsset(data.icon_url, revision),
    brand_color: normalizeHex(data.brand_color) || DEFAULT_BRAND,
    brand_theme: {
      secondary_color: normalizeHex(theme.secondary_color) || '',
      navigation_style: NAVIGATION_STYLES.includes(theme.navigation_style) ? theme.navigation_style : 'neutral',
      surface_style: theme.surface_style === 'tint' ? 'tint' : 'neutral',
    },
    chart_colors: Array.isArray(data.chart_colors) ? data.chart_colors.map(normalizeHex).filter(Boolean).slice(0, 20) : [],
    show_graphit_branding: data.show_graphit_branding !== false,
    revision,
  }
}

function channels(hex) {
  return hex.slice(1).match(/../g).map((value) => parseInt(value, 16))
}

function mix(first, second, amount) {
  const right = channels(second)
  return '#' + channels(first).map((value, index) => Math.round(value * (1 - amount) + right[index] * amount).toString(16).padStart(2, '0')).join('')
}

function luminance(hex) {
  const [red, green, blue] = channels(hex).map((value) => {
    const channel = value / 255
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  })
  return red * 0.2126 + green * 0.7152 + blue * 0.0722
}

export function contrastRatio(first, second) {
  const a = luminance(first)
  const b = luminance(second)
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

function foreground(background) {
  return contrastRatio('#ffffff', background) >= contrastRatio('#000000', background) ? '#ffffff' : '#000000'
}

/** Keep the hue where possible, moving only as far as necessary for readable text. */
function readable(color, background, ratio = 4.5) {
  if (contrastRatio(color, background) >= ratio) return color
  const target = foreground(background)
  for (let step = 1; step <= 100; step += 1) {
    const candidate = mix(color, target, step / 100)
    if (contrastRatio(candidate, background) >= ratio) return candidate
  }
  return target
}

/**
 * Minimum oklch lightness delta `--color-navigation-active` must keep from
 * `--color-navigation` (F-12: the active pill was reading as nearly
 * invisible for brand/style combinations where the brand hue's own
 * lightness landed close to navigation's — most visibly a "solid"
 * navigation style with no explicit secondary color, where the active pill
 * mixed toward the SAME hex as navigation itself). Dark surfaces need a
 * bigger jump than light ones to read as clearly "lit" rather than a
 * rounding artifact (visual-direction.md's own dark-mode lift pattern).
 */
export const MIN_NAVIGATION_ACTIVE_DELTA_L = { light: 0.07, dark: 0.10 }

// oklch(from ...) round-trips through an 8-bit-per-channel #rrggbb, which can
// land a target lightness a hair under the floor after re-quantizing; this
// buffer keeps the realized delta at or above MIN_NAVIGATION_ACTIVE_DELTA_L.
const ACTIVE_DELTA_L_BUFFER = 0.012

function clampLightness(value) {
  return Math.min(0.97, Math.max(0.03, value))
}

/**
 * Push `hex`'s oklch lightness at least `MIN_NAVIGATION_ACTIVE_DELTA_L` away
 * from `referenceHex`'s, keeping `hex`'s own hue/chroma (so the brand tint
 * survives) — lightening dark navigation, darkening light navigation, falling back
 * to the other direction only if the preferred one would clip at the gamut
 * edge without reaching the floor.
 *
 * @param {string} hex - candidate colour (already carries the brand hue)
 * @param {string} referenceHex - the background it must stay distinct from
 * @param {boolean} dark
 * @returns {string} `#rrggbb`
 */
function withMinLightnessDelta(hex, referenceHex, dark) {
  const floor = MIN_NAVIGATION_ACTIVE_DELTA_L[dark ? 'dark' : 'light']
  const buffered = floor + ACTIVE_DELTA_L_BUFFER
  // Navigation may be ink-colored in a light workspace or a pale tenant
  // brand in a dark one. Its own surface determines the direction.
  const direction = foreground(referenceHex) === '#ffffff' ? 1 : -1
  const { L: referenceL } = hexToOklch(referenceHex)
  const { C, H } = hexToOklch(hex)
  let targetL = clampLightness(referenceL + direction * buffered)
  if (Math.abs(targetL - referenceL) < floor) {
    targetL = clampLightness(referenceL - direction * buffered)
  }
  return oklchToHex(targetL, C, H)
}

/**
 * Oklch chroma under which a brand reads as grey (slate, zinc, the neutral
 * fallback). Lifting such a brand for dark surfaces lands on a mid grey
 * that, with dark text, looks like a disabled button.
 */
export const NEUTRAL_BRAND_MAX_CHROMA = 0.05

/**
 * The dark-mode primary fill. A coloured brand is lifted a quarter toward
 * white (then held to readable contrast by the caller). A neutral brand has
 * no hue to keep, so it takes the page ink instead: a near-white fill with
 * dark text, as strong as a primary action should look, never a grey one.
 *
 * @param {string} primary - the brand
 * @param {Object} palette - the dark palette
 * @returns {{solid: string, isNeutral: boolean}}
 */
function darkSolid(primary, palette) {
  const isNeutral = hexToOklch(primary).C < NEUTRAL_BRAND_MAX_CHROMA
  return { solid: isNeutral ? palette.ink : mix(primary, '#ffffff', 0.25), isNeutral }
}

/** Complete brand/navigation/surface map, shared by the application and draft previews. */

/**
 * Minimum oklch lightness the canvas must sit BELOW the card surface, per
 * scheme (F-13). A tinted canvas mixes toward the brand, which lifts a dark
 * canvas onto the surface's own lightness and erases card edges entirely.
 */
export const MIN_CANVAS_SURFACE_DELTA_L = { light: 0.02, dark: 0.03 }

function withMinCanvasContrast(canvasHex, surfaceHex, dark) {
  const floor = MIN_CANVAS_SURFACE_DELTA_L[dark ? 'dark' : 'light']
  const { L: surfaceL } = hexToOklch(surfaceHex)
  const { L, C, H } = hexToOklch(canvasHex)
  if (surfaceL - L >= floor) return canvasHex
  return oklchToHex(clampLightness(surfaceL - floor - ACTIVE_DELTA_L_BUFFER), C, H)
}

export function brandingTokens(input, resolved = 'light') {
  const profile = normalizeBranding(input)
  const dark = resolved === 'dark'
  const palette = PALETTES[dark ? 'dark' : 'light']
  const primary = profile.brand_color
  const secondary = profile.brand_theme.secondary_color || primary
  const tinted = profile.brand_theme.surface_style === 'tint'
  const surface = palette.surface
  const canvas = withMinCanvasContrast(
    tinted ? mix(palette.canvas, primary, dark ? 0.09 : 0.035) : palette.canvas,
    surface,
    dark
  )
  const surface2 = tinted ? mix(palette.surface2, primary, 0.06) : palette.surface2
  const textOnWorkSurfaces = (color) => [surface, surface2, canvas].reduce((candidate, background) => readable(candidate, background), color)
  // The same accent is also used as link text and focus rings on page surfaces.
  const { solid: darkFill, isNeutral } = dark ? darkSolid(primary, palette) : { solid: primary, isNeutral: false }
  let solid = darkFill
  for (const background of [surface, surface2, canvas]) solid = readable(solid, background)
  // A near-white neutral fill can't get lighter on hover: it dims toward the surface instead.
  const hoverTarget = isNeutral ? surface : dark ? '#ffffff' : '#000000'
  const hover = readable(mix(solid, hoverTarget, isNeutral ? 0.10 : 0.08), surface)
  const active = readable(mix(solid, hoverTarget, isNeutral ? 0.18 : 0.14), surface)
  const subtle = mix(surface, primary, dark ? 0.18 : 0.10)
  const style = profile.brand_theme.navigation_style
  // The neutral rail frames the workspace in ink; saved tint/solid choices
  // retain their tenant-led treatment. Navigation colors always resolve
  // against that actual surface, independently of the page's theme.
  const navigation = style === 'solid' ? primary : style === 'tint' ? mix(surface, primary, dark ? 0.17 : 0.09) : palette.navigation
  const navigationIsDark = foreground(navigation) === '#ffffff'
  const navInk = readable(PALETTES[navigationIsDark ? 'dark' : 'light'].ink, navigation)
  const navigationLine = mix(navigation, navInk, navigationIsDark ? 0.08 : 0.10)
  // Active pill (F-12): mix toward secondary for the brand hue/chroma, same
  // as before, then force the result's lightness away from navigation's own
  // by at least MIN_NAVIGATION_ACTIVE_DELTA_L — mixing toward secondary
  // alone could land almost exactly on navigation's lightness (e.g. "solid"
  // style with no secondary set mixes primary toward itself, i.e. no-op).
  const navActive = withMinLightnessDelta(mix(navigation, secondary, style === 'solid' ? 0.30 : 0.15), navigation, dark)
  return {
    '--brand': primary,
    '--brand-solid': solid,
    '--brand-hover': hover,
    '--brand-active': active,
    '--brand-subtle': subtle,
    '--brand-ink': readable(primary, subtle),
    '--brand-fg': foreground(solid),
    '--brand-secondary': secondary,
    '--color-surface': surface,
    '--color-surface-2': surface2,
    '--color-canvas': canvas,
    '--color-ink': palette.ink,
    '--color-ink-2': textOnWorkSurfaces(palette.muted),
    '--color-ink-3': textOnWorkSurfaces(palette.tertiary),
    '--color-ink-placeholder': textOnWorkSurfaces(palette.placeholder),
    '--color-line': palette.line,
    '--color-navigation': navigation,
    '--color-navigation-ink': navInk,
    '--color-navigation-muted': readable(mix(navigation, navInk, 0.72), navigation),
    '--color-navigation-line': navigationLine,
    '--color-navigation-active': navActive,
    '--color-navigation-active-ink': readable(navInk, navActive),
  }
}
